import axios, { isAxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.cecurestream.com';
const ADMIN_API_URL = `${API_BASE_URL}/admin`;

// Admin auth token management
const ADMIN_TOKEN_KEY = 'adminAuthToken';
const ADMIN_USER_KEY = 'adminUser';

export interface AdminUser {
  username: string;
  email: string;
  role: 'super_admin' | 'admin' | 'analyst' | 'support' | 'viewer';
  permissions: string[];
  department?: string;
  sub: string;
}

export interface AdminAuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// Save admin auth data
export const saveAdminAuthData = (tokens: AdminAuthTokens, user: AdminUser) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, JSON.stringify(tokens));
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
};

// Get admin auth tokens
export const getAdminAuthTokens = (): AdminAuthTokens | null => {
  const tokens = localStorage.getItem(ADMIN_TOKEN_KEY);
  return tokens ? JSON.parse(tokens) : null;
};

// Get admin user
export const getAdminUser = (): AdminUser | null => {
  const user = localStorage.getItem(ADMIN_USER_KEY);
  return user ? JSON.parse(user) : null;
};

// Clear admin auth data
export const clearAdminAuthData = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
};

// Check if admin is authenticated
export const isAdminAuthenticated = (): boolean => {
  const tokens = getAdminAuthTokens();
  if (!tokens) return false;
  
  // Check if token is expired
  const expirationTime = tokens.expiresIn * 1000; // Convert to milliseconds
  const isExpired = Date.now() > expirationTime;
  
  return !isExpired;
};

// Admin login
export const adminLogin = async (email: string, password: string) => {
  try {
    const response = await axios.post(`${ADMIN_API_URL}/auth/login`, {
      email,
      password
    });
    
    const data = response.data;
    
    // Check if new password is required
    if (data.requiresNewPassword) {
      return {
        success: false,
        requiresNewPassword: true,
        session: data.session,
        email: data.email
      };
    }
    
    // Check if MFA is required
    if (data.requiresMFA) {
      return {
        success: false,
        requiresMFA: true,
        session: data.session,
        email: data.email
      };
    }
    
    // Successful login
    const { tokens, user } = data;
    saveAdminAuthData(tokens, user);
    
    return { success: true, user };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
    return {
      success: false,
      error: 'Login failed'
    };
  }
};

// Admin logout
export const adminLogout = () => {
  clearAdminAuthData();
  window.location.href = '/admin/login';
};

// Refresh admin token
export const refreshAdminToken = async () => {
  const tokens = getAdminAuthTokens();
  if (!tokens) return null;
  
  try {
    const response = await axios.post(`${ADMIN_API_URL}/auth/refresh`, {
      refreshToken: tokens.refreshToken
    });
    
    const newTokens = response.data.tokens;
    const user = getAdminUser();
    if (user) {
      saveAdminAuthData(newTokens, user);
    }
    
    return newTokens;
  } catch (error) {
    clearAdminAuthData();
    return null;
  }
};

// Check admin permissions
export const hasAdminPermission = (permission: string): boolean => {
  const user = getAdminUser();
  if (!user) return false;
  
  // Super admin has all permissions
  if (user.role === 'super_admin') return true;
  
  // Check specific permission
  return user.permissions.includes(permission) || user.permissions.includes('*');
};

// Get admin role display name
export const getAdminRoleDisplayName = (role: string): string => {
  const roleNames: Record<string, string> = {
    super_admin: 'Super Admin',
    admin: 'Administrator',
    analyst: 'Analyst',
    support: 'Support',
    viewer: 'Viewer'
  };
  
  return roleNames[role] || role;
};

// Admin MFA verification
export const adminVerifyMFA = async (session: string, mfaCode: string, email: string) => {
  try {
    const response = await axios.post(`${ADMIN_API_URL}/auth/mfa`, {
      session,
      mfaCode,
      email
    });
    
    const { tokens, user } = response.data;
    saveAdminAuthData(tokens, user);
    
    return { success: true, user };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        error: error.response?.data?.error || 'MFA verification failed'
      };
    }
    return {
      success: false,
      error: 'MFA verification failed'
    };
  }
};

// Admin change password (for new users)
export const adminChangePassword = async (session: string, email: string, newPassword: string) => {
  try {
    const response = await axios.post(`${ADMIN_API_URL}/auth/change-password`, {
      session,
      email,
      newPassword
    });
    
    const data = response.data;
    
    // Check if MFA setup is required
    if (data.requiresMFASetup) {
      return {
        success: false,
        requiresMFASetup: true,
        session: data.session,
        secretCode: data.secretCode,
        email: data.email
      };
    }
    
    // Check if MFA is required
    if (data.requiresMFA) {
      return {
        success: false,
        requiresMFA: true,
        session: data.session,
        email: data.email
      };
    }
    
    // Successful password change
    const { tokens, user } = data;
    saveAdminAuthData(tokens, user);
    
    return { success: true, user };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to change password'
      };
    }
    return {
      success: false,
      error: 'Failed to change password'
    };
  }
};